/**
 * FilePreviewModal — THE file viewer (FILES-04, Seth 2026-06-11; FILES-EDIT-01,
 * Seth 2026-09-28: "Will there be an easy way to download, edit, markup,
 * rename, etc from the office?"). Clicking any file anywhere — the job's
 * Documents, the AHRI certificate, the 360 Files card and Documents tab, the
 * dispatch peek, the timeline — opens it here:
 *
 *   · preview (photos, PDFs), what the AI read off it, where it came from
 *   · Rename · Type + crew switch (job files) · Mark Up (photos and PDF pages
 *     → a marked-up copy, the original stays) · New Version (upload the edited
 *     file; the old one is archived and one click away) · Remove (two clicks)
 *   · Download (under its name) · Open in New Tab · Email It · Text It
 *
 * Competitor frame: ServiceTitan annotates images / PDFs and keeps versions;
 * Jobber's library downloads but can't rename or mark up; Buildertrend marks
 * up with version history. Pegasus: all of it in one window, no folders —
 * the document TYPE is the folder.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight, ClipboardList, Download, ExternalLink, Eye, EyeOff, FileText, FolderOpen, History, Loader2, Mail,
  MessageSquare, PenLine, Pencil, Receipt, RefreshCw, Sparkles, Trash2, Upload,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { toastError } from "@/components/ui/mutationToast";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import { DispatchPeek } from "@/features/customer-360/DispatchPeek";
import {
  BUCKET, crewCanSee, downloadFile, fileWhen, fileWho, isImageFile, isPdfFile, jobDocTypeOf, prettySize,
  useArchiveFile, useFileRow, useJobDocTypes, useRenameFile, useReplaceFile, useSetFileCrewVisible, useSetFileDocTag,
  useSortFiles, JOB_DOC_TYPES_DEFAULT, type PegasusFile,
} from "./filesCore";
import { MarkupEditor } from "./MarkupEditor";
import {
  ComposeEmailModal,
  ComposeTextModal,
} from "@/features/customer-360/CustomerMessages";

const SHARE_TTL_SECONDS = 7 * 24 * 3600; // 7 days

const actionBtn =
  "inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50";

export function FilePreviewModal({
  file,
  custno,
  onClose,
  siblings,
}: {
  file: PegasusFile | null;
  custno: string;
  onClose: () => void;
  /** JOB-SOLD-01: flip through a set (the sale's photos) with ← → and Previous / Next. */
  siblings?: PegasusFile[];
}) {
  // Another file can take the window's place — the original of a marked-up
  // copy, the version a file replaced, the copy just saved.
  const [openId, setOpenId] = useState<string | null>(null);
  // Where "Back" returns to after opening an original / an earlier version.
  const [backTo, setBackTo] = useState<{ id: string; label: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [lastFileId, setLastFileId] = useState<string | null>(file?.id ?? null);
  if ((file?.id ?? null) !== lastFileId) {
    setLastFileId(file?.id ?? null);
    setOpenId(null);
    setBackTo(null);
    setNote(null);
  }
  const shownId = openId ?? file?.id ?? null;
  const { data: fresh } = useFileRow(shownId);
  const f = fresh ?? (shownId === file?.id ? file : null);

  const [url, setUrl] = useState<string | null>(null);
  const [share, setShare] = useState<"email" | "text" | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [peekDispatch, setPeekDispatch] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [markup, setMarkup] = useState(false);
  const [sureRemove, setSureRemove] = useState(false);
  const pickVersion = useRef<HTMLInputElement>(null);
  // One new version per pick, even if the browser reports the pick twice.
  const replacing = useRef(false);
  const navigate = useNavigateWithBack();
  const { pathname } = useLocation();
  const rename = useRenameFile();
  const replace = useReplaceFile();
  const archive = useArchiveFile();
  const setTag = useSetFileDocTag();
  const setCrew = useSetFileCrewVisible();
  const sort = useSortFiles();
  const { data: typesQ } = useJobDocTypes();
  const types = typesQ ?? JOB_DOC_TYPES_DEFAULT;
  const { data: related } = useFileRow(f?.markup_of ?? f?.version_of ?? null);
  // Previous / Next through the set this file was opened from.
  const set = siblings && siblings.length > 1 ? siblings : null;
  const at = set && f ? set.findIndex((x) => x.id === f.id) : -1;
  const step = (d: -1 | 1) => {
    if (!set || at < 0) return;
    const next = set[(at + d + set.length) % set.length];
    setBackTo(null);
    setNote(null);
    setOpenId(next.id === file?.id ? null : next.id);
  };
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    if (!set) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); stepRef.current(-1); }
      if (e.key === "ArrowRight") { e.preventDefault(); stepRef.current(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [set]);

  // Short-lived URL for viewing; minted fresh per file.
  const path = f?.storage_path ?? null;
  useEffect(() => {
    setUrl(null);
    setShare(null);
    setShareLink(null);
    setPeekDispatch(null); // don't carry a prior file's open dispatch peek onto the next file
    setRenaming(null);
    setSureRemove(false);
    if (!path) return;
    let cancelled = false;
    supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, 3600)
      .then(({ data }) => {
        if (!cancelled) setUrl(data?.signedUrl ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (!file || !f) return null;

  const isImage = isImageFile(f);
  const isPdf = isPdfFile(f);
  const canMark = !f.archived && (isImage || isPdf);
  const jobFile = !!f.job_id;
  const type = jobDocTypeOf(f, types);
  const crew = crewCanSee(f, types);
  const sorting = !f.ai_at && !f.doc_tag && jobFile && Date.now() - new Date(f.created_at).getTime() < 3 * 60 * 1000;

  async function startShare(kind: "email" | "text") {
    // 7-day link so the customer isn't racing an expiry.
    const { data } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(f!.storage_path, SHARE_TTL_SECONDS);
    if (!data?.signedUrl) return;
    setShareLink(data.signedUrl);
    setShare(kind);
  }

  const saveName = () => {
    const v = (renaming ?? "").trim();
    if (!v || v === f.filename) { setRenaming(null); return; }
    rename.mutate({ id: f.id, filename: v, was: f.filename }, {
      onSuccess: () => setRenaming(null),
      onError: (e) => toastError(e instanceof Error ? e.message : "Couldn't rename it."),
    });
  };

  const shareBody = shareLink
    ? `Here's "${f.filename}" from Sedgwick Heating:\n${shareLink}\n\n(The link works for 7 days.)`
    : "";

  const title = renaming != null ? (
    <span className="flex items-center gap-2">
      <input
        autoFocus
        value={renaming}
        onChange={(e) => setRenaming(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); saveName(); }
          if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setRenaming(null); }
        }}
        aria-label="File name"
        className="w-[min(32rem,60vw)] rounded-md border border-brand-300 px-2 py-1 text-base font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
      <button type="button" onClick={saveName} disabled={rename.isPending}
        className="rounded-md bg-brand-600 px-2.5 py-1 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
        {rename.isPending ? "Saving…" : "Save"}
      </button>
    </span>
  ) : (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 break-all">{f.filename}</span>
      {!f.archived && (
        <button type="button" onClick={() => setRenaming(f.filename.replace(/\.[A-Za-z0-9]{1,6}$/, ""))}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-semibold text-brand-700 hover:bg-brand-50">
          <Pencil className="h-3.5 w-3.5" /> Rename
        </button>
      )}
    </span>
  );

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={title}
        subtitle={[prettySize(f.size_bytes), `added by ${fileWho(f)}`, fileWhen(f.created_at), f.source === "esc" ? "brought over from ESC" : null]
          .filter(Boolean).join(" · ")}
        size="xl"
        footer={
          <div className="flex flex-wrap items-center gap-2">
            {!f.archived && (
              <>
                {canMark && (
                  <button type="button" onClick={() => setMarkup(true)} className={actionBtn}>
                    <PenLine className="h-4 w-4" /> Mark Up
                  </button>
                )}
                <button type="button" onClick={() => pickVersion.current?.click()} disabled={replace.isPending} className={actionBtn}
                  title="Edited it on your computer? Upload the new copy — it takes this one's place and this one is kept as the earlier version.">
                  {replace.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} New Version
                </button>
                <input ref={pickVersion} type="file" className="hidden"
                  onChange={(e) => {
                    const nf = e.target.files?.[0];
                    e.target.value = "";
                    if (!nf || replacing.current) return;
                    replacing.current = true;
                    replace.mutate({ old: f, file: nf }, {
                      onSuccess: (row) => { setBackTo(null); setOpenId(row.id); setNote("New version saved — the earlier one is kept."); },
                      onError: (er) => toastError(er instanceof Error ? `Couldn't upload it — ${er.message}` : "Couldn't upload it."),
                      onSettled: () => { replacing.current = false; },
                    });
                  }} />
                {sureRemove ? (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <button type="button" disabled={archive.isPending}
                      onClick={() => archive.mutate(f.id, {
                        onSuccess: onClose,
                        onError: () => toastError(`Couldn't remove ${f.filename} — it's still on the record.`),
                      })}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 font-semibold text-white hover:bg-rose-700">Remove It</button>
                    <button type="button" onClick={() => setSureRemove(false)} className="rounded-lg px-2 py-1.5 font-medium text-slate-500 hover:bg-slate-100">Keep</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setSureRemove(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600">
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                )}
              </>
            )}
            <span className="ml-auto flex flex-wrap items-center gap-2">
              <button type="button"
                onClick={() => void downloadFile(f).catch(() => toastError("Couldn't download it — try again."))}
                className={actionBtn}>
                <Download className="h-4 w-4" /> Download
              </button>
              <button type="button" onClick={() => url && window.open(url, "_blank", "noopener")} disabled={!url} className={actionBtn}>
                <ExternalLink className="h-4 w-4" /> Open in New Tab
              </button>
              <button type="button" onClick={() => startShare("email")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800">
                <Mail className="h-4 w-4" /> Email It
              </button>
              <button type="button" onClick={() => startShare("text")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800">
                <MessageSquare className="h-4 w-4" /> Text It
              </button>
            </span>
          </div>
        }
      >
        {f.archived && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <History className="h-4 w-4" /> This is an earlier version — it was replaced or removed.
          </div>
        )}
        {backTo && (
          <div className="mb-3">
            <button type="button" onClick={() => { setOpenId(backTo.id); setBackTo(null); }}
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline">
              ← Back to {backTo.label}
            </button>
          </div>
        )}
        {note && <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">{note}</p>}

        {/* What it is, where it lives */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {jobFile && !f.archived && (
            <span className="inline-flex items-center gap-1.5">
              <FolderOpen className="h-4 w-4 text-slate-400" />
              {sorting ? (
                <span className="inline-flex items-center gap-1 text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sorting…</span>
              ) : (
                <select value={type} aria-label="Document type" disabled={setTag.isPending}
                  onChange={(e) => setTag.mutate({ id: f.id, doc_tag: e.target.value })}
                  className="rounded-md border border-hairline bg-white px-2 py-1 text-sm font-semibold text-slate-700 focus:border-brand-500 focus:outline-none">
                  {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              )}
              {f.tag_by === "ai" && !sorting && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-700" title="The AI picked this type — change it if it's wrong">
                  <Sparkles className="h-3.5 w-3.5" /> Sorted by AI
                </span>
              )}
            </span>
          )}
          {jobFile && !f.archived && (
            <button type="button" onClick={() => setCrew.mutate({ id: f.id, crew_visible: !crew })}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
                crew ? "bg-brand-50 text-brand-700 hover:bg-brand-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>
              {crew ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {crew ? "The crew can see it" : "Office only"}
            </button>
          )}
          {(f.dispatch || f.invoice || (f.job_id && !pathname.includes(f.job_id))) && (
            <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
              {f.job_id && !pathname.includes(f.job_id) && (
                <button type="button" onClick={() => { navigate(`/jobs/${f.job_id}?tab=workorder`); onClose(); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1 font-medium text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50">
                  <FolderOpen className="h-3.5 w-3.5" /> Job <ArrowRight className="h-3 w-3" />
                </button>
              )}
              {f.dispatch && !f.dispatch.startsWith("job-") && (
                <button type="button" onClick={() => setPeekDispatch(f.dispatch)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1 font-medium text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50">
                  <ClipboardList className="h-3.5 w-3.5" /> Call <ArrowRight className="h-3 w-3" />
                </button>
              )}
              {f.invoice && (
                <button type="button" onClick={() => { navigate(`/invoice/${f.invoice}`); onClose(); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1 font-medium text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50">
                  <Receipt className="h-3.5 w-3.5" /> Invoice #{f.invoice} <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </span>
          )}
        </div>
        {(f.ai_summary || f.caption) && (
          <div className="mb-3 space-y-1 text-sm">
            {f.ai_summary && (
              <p className="flex items-start gap-1.5 text-slate-700">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" /> {f.ai_summary}
              </p>
            )}
            {f.caption && <p className="text-slate-500">{f.caption}</p>}
          </div>
        )}
        {related && (
          <p className="mb-3 flex flex-wrap items-center gap-1.5 text-sm text-slate-600">
            <History className="h-4 w-4 text-slate-400" />
            {f.markup_of ? "A marked-up copy of" : "Replaced"} <span className="font-medium text-slate-800">{related.filename}</span>
            <button type="button" onClick={() => { setBackTo({ id: f.id, label: f.filename }); setNote(null); setOpenId(related.id); }} className="font-semibold text-brand-700 hover:underline">
              {f.markup_of ? "Open the original" : "Open the earlier version"}
            </button>
          </p>
        )}

        {set && at >= 0 && (
          <div className="mb-2 flex items-center justify-between gap-3">
            <button type="button" onClick={() => step(-1)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900">
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <span className="text-sm font-medium tabular-nums text-slate-500">{at + 1} of {set.length}</span>
            <button type="button" onClick={() => step(1)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        {/* The window opens with focus here (not on the type picker — an arrow key there would change the type). */}
        <div data-autofocus tabIndex={-1} className="flex min-h-[40vh] items-center justify-center rounded-lg bg-slate-50 outline-none">
          {!url ? (
            <div className="h-40 w-full animate-pulse rounded-lg bg-slate-100" />
          ) : isImage ? (
            <img
              src={url}
              alt={f.ai_summary ?? f.caption ?? f.filename}
              className="max-h-[60vh] w-auto max-w-full rounded-lg object-contain"
            />
          ) : isPdf ? (
            <iframe
              src={url}
              title={f.filename}
              className="h-[60vh] w-full rounded-lg"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 py-12 text-slate-500">
              <FileText className="h-10 w-10" />
              <p className="text-sm">No preview for this kind of file — Download opens it in its own program.</p>
            </div>
          )}
        </div>
        {jobFile && !f.archived && !sorting && (isImage || isPdf) && (
          <div className="mt-2 flex justify-end">
            <button type="button" disabled={sort.isPending} onClick={() => sort.mutate({ ids: [f.id], force: true })}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition-colors hover:text-violet-700 disabled:opacity-50">
              {sort.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Sort It Again
            </button>
          </div>
        )}
      </Modal>

      {markup && (
        <MarkupEditor
          file={f}
          onClose={() => setMarkup(false)}
          onSaved={(copy) => { setMarkup(false); setBackTo(null); setOpenId(copy.id); setNote("Marked-up copy saved — the original is unchanged."); }}
        />
      )}
      {share === "email" && (
        <ComposeEmailModal
          custno={custno}
          initialSubject={`${f.filename} — Sedgwick Heating`}
          initialBody={shareBody}
          onClose={() => setShare(null)}
        />
      )}
      {share === "text" && (
        <ComposeTextModal
          custno={custno}
          initialBody={shareBody}
          onClose={() => setShare(null)}
        />
      )}
      {/* Click a doc's source dispatch → it slides out right over the preview. */}
      <DispatchPeek dispatchId={peekDispatch} onClose={() => setPeekDispatch(null)} />
    </>
  );
}
